import { useState, useEffect, useRef, useCallback } from 'react';
import { draftSet, draftClear, registerDraftFlusher, recoveryFlagClear } from '../services/outbox';

/**
 * Shared state + behavior for the block editor — consumed by BlockEditorPanel.
 *
 * Owning the magic-word auto-save, IME composition, draft recovery, and
 * timeline-menu logic here keeps the two render paths from drifting apart.
 *
 * Args:
 *  - show            : boolean — editor is visible
 *  - initialContent  : string  — pre-fill textarea (empty for new block)
 *  - onConfirm       : (content: string) => Promise<void> — called on submit
 *  - onClose         : () => void — called on cancel / dismiss
 *  - autoSaveWord    : string  — trigger word for auto-save
 *  - autoSaveDelay   : number  — ms delay before auto-save fires
 *  - filePath        : string  — file being edited (draft recovery)
 *  - blockIndex      : number|null — block index being edited (draft recovery)
 *  - originalContent : string|null — original block text (draft fingerprint)
 */
export default function useBlockEdit({
  show,
  initialContent = '',
  onConfirm,
  onClose,
  autoSaveWord = 'over',
  autoSaveDelay = 3000,
  filePath = '',
  blockIndex = null,
  originalContent = null,
}) {
  const isEdit = !!initialContent;
  const [content, setContent] = useState(initialContent);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  // Ref to always hold the latest content — avoids closure staleness
  // inside the 1.5 s auto-save timer.
  const contentRef = useRef(initialContent);

  // Tracks IME composition (soft keyboards / autocorrect / swipe typing).
  // While true the textarea value is not final — skip magic-word detection.
  const composingRef = useRef(false);

  // Auto-save debounce timer
  const autoSaveRef = useRef(null);

  // ── Waiting-to-confirm (magic word) state ──────────────────
  // When the user's text ends with the magic word, we enter a countdown
  // instead of saving silently. A progress bar fills in the header and
  // Save / Cancel buttons appear. The user can:
  //   Save   → save immediately (skip delay)
  //   Cancel → untrigger the countdown, keep the editor and magic word open
  //   any key → cancel waiting, keep magic word, resume editing
  //   timeout → auto-save (strips magic word, as before)
  const [waitingToConfirm, setWaitingToConfirm] = useState(false);

  // ── Timeline mark menu — a single clock that opens a dropdown of two
  // actions (mark time / move to timeline). ──
  const [timelineMenuOpen, setTimelineMenuOpen] = useState(false);
  const tlMenuRef = useRef(null);

  // ── Draft (survives session expiry, stored in the outbox layer) ──
  // Stored as { filePath, blockIndex, originalContent, newContent, timestamp }
  // so recovery can restore an edit *in place* (index + content fingerprint)
  // instead of always treating it as a brand-new block.
  const showRef = useRef(show);
  useEffect(() => { showRef.current = show; }, [show]);

  // Single clear path — drops both the draft and the recovery flag so no close
  // path leaves either behind.  Awaited in handleConfirm so the delete commits
  // before the editor is dismissed; the pagehide flush covers the fire-and-forget
  // paths (cancel / parent-driven close) right before unload.
  const resolveDraft = useCallback(async () => {
    await Promise.all([draftClear(), recoveryFlagClear()]);
  }, []);

  // Clear the safety-net draft whenever the editor transitions to closed,
  // regardless of which path closed it.  handleClose / handleConfirm already
  // clear on their own paths, but the parent-driven paths (delete, timeline
  // mark, move-to-destination) close the editor directly and would otherwise
  // leave a stale draft that resurfaces as a spurious "recovered content"
  // popup on the next load.  Guarded on the shown→closed transition so a draft
  // recovered from a previous session expiry isn't wiped on mount.
  const prevShowRef = useRef(show);
  useEffect(() => {
    const wasShown = prevShowRef.current;
    prevShowRef.current = show;
    if (wasShown && !show) {
      resolveDraft();
    }
  }, [show, resolveDraft]);

  const writeDraft = useCallback(() => {
    if (!showRef.current) return;
    const val = contentRef.current.trim();
    if (!val) {
      draftClear();
      return;
    }
    draftSet({
      filePath: filePath || '',
      blockIndex: blockIndex ?? null,
      originalContent: originalContent || null,
      newContent: val,
      timestamp: Date.now(),
    });
  }, [filePath, blockIndex, originalContent]);

  // Debounce-write on every keystroke — the draft is always ~latest.
  const draftTimerRef = useRef(null);
  useEffect(() => {
    if (!show) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(writeDraft, 300);
    return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
  }, [content, show, writeDraft]);

  // Immediate flush when a session expiry is announced.
  useEffect(() => {
    const handler = () => writeDraft();
    window.addEventListener('session-expiring', handler);
    return () => window.removeEventListener('session-expiring', handler);
  }, [writeDraft]);

  // Register the flusher so AppLayout's confirm dialog can await a final write.
  useEffect(() => {
    registerDraftFlusher(writeDraft);
    return () => registerDraftFlusher(null);
  }, [writeDraft]);

  // Flush a final clear on page hide / background so a save-or-cancel that
  // happened moments before a tab close still commits (IndexedDB/Capacitor
  // deletes are async).  Only clears when the editor is already closed — an
  // open editor's in-progress draft is left intact for recovery.
  useEffect(() => {
    const flush = () => {
      if (!showRef.current) resolveDraft();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', flush);
    };
  }, [resolveDraft]);

  // Reset content when the editor opens with new initialContent.
  // (Draft recovery is handled by the parent — which knows the file's block
  // array and can decide in-place vs new-block replacement.)
  useEffect(() => {
    if (show) {
      const initial = initialContent;
      setContent(initial);
      contentRef.current = initial;
      setSaving(false);
      setWaitingToConfirm(false);
      setTimelineMenuOpen(false);
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current);
        autoSaveRef.current = null;
      }
      // Auto-focus the textarea and place cursor at the end
      const timer = setTimeout(() => {
        const ta = textareaRef.current;
        if (ta) {
          ta.focus();
          ta.setSelectionRange(ta.value.length, ta.value.length);
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [show, initialContent]);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current);
        autoSaveRef.current = null;
      }
    };
  }, []);

  // Close the timeline menu on outside click / tap.
  useEffect(() => {
    if (!timelineMenuOpen) return;
    const onDocClick = (e) => {
      if (tlMenuRef.current && !tlMenuRef.current.contains(e.target)) {
        setTimelineMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [timelineMenuOpen]);

  // Prevent closing while a save is in-flight — the user must wait
  // for it to succeed or fail before dismissing the editor.
  const handleClose = () => {
    if (saving) return;
    resolveDraft(); // edit resolved (cancelled) — drop draft + recovery flag
    onClose();
  };

  const handleConfirm = async () => {
    const trimmed = contentRef.current.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    // Await the clear so the draft delete commits before the editor dismisses
    // and (potentially) the page unloads — no stale draft survives a save.
    await resolveDraft();
    // Close the editor immediately to prevent multiple clicks/requests.
    // The save runs in the background; errors are logged but the editor
    // is already dismissed — the caller's onConfirm is responsible for
    // surfacing errors (e.g. via a toast).
    onClose();
    try {
      await onConfirm(trimmed);
    } catch {
      // Editor is already closed; error handling is the caller's responsibility.
    }
  };

  // ── Cancel waiting-to-confirm mode ─────────────────────────
  const cancelWaiting = () => {
    setWaitingToConfirm(false);
    if (autoSaveRef.current) {
      clearTimeout(autoSaveRef.current);
      autoSaveRef.current = null;
    }
  };

  // ── Execute the auto-save: strip magic word, save, close ──
  const executeAutoSave = () => {
    cancelWaiting();
    const trimmed = contentRef.current.trimEnd();
    const word = autoSaveWord.trim();
    let contentWithoutWord = trimmed;
    if (word && trimmed.toLowerCase().endsWith(word.toLowerCase())) {
      contentWithoutWord = trimmed.slice(0, trimmed.length - word.length).trimEnd();
    }
    contentRef.current = contentWithoutWord;
    setContent(contentWithoutWord);
    // Defer so React can flush the state update before handleConfirm closes the editor
    setTimeout(() => handleConfirm(), 0);
  };

  // ── Enter waiting-to-confirm mode when the text ends with the magic word ──
  const checkMagicWord = (val) => {
    // Trigger whenever the value ends with the configured word (case
    // insensitive), e.g. "aaaaaover" ends with "over" and fires. Works for
    // both new block creation and editing an existing block.
    const word = autoSaveWord.trim();
    if (!word) return;
    const trimmed = val.trimEnd();
    if (trimmed.toLowerCase().endsWith(word.toLowerCase())) {
      setWaitingToConfirm(true);

      // Start the auto-save timer
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
      autoSaveRef.current = setTimeout(() => {
        executeAutoSave();
      }, autoSaveDelay);
    }
  };

  // ── IME composition — the soft keyboard is still composing the value
  // (autocorrect, autocomplete, swipe typing). Detect the magic word only
  // after the text is committed, not on intermediate composition values.
  const handleCompositionStart = () => {
    composingRef.current = true;
  };

  const handleCompositionEnd = () => {
    composingRef.current = false;
    // The composed text is now committed — re-check against the final value.
    const ta = textareaRef.current;
    if (ta) checkMagicWord(ta.value);
  };

  const handleChange = (e) => {
    let val = e.target.value;

    // While composing, just reflect the text — don't detect the magic word
    // or cancel the countdown based on mid-composition values.
    if (composingRef.current) {
      contentRef.current = val;
      setContent(val);
      return;
    }

    // Any typing while waiting cancels the countdown.
    if (waitingToConfirm) {
      cancelWaiting();
    } else {
      // Clear any pending auto-save timer on every keystroke
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current);
        autoSaveRef.current = null;
      }
    }

    contentRef.current = val;
    setContent(val);

    checkMagicWord(val);
  };

  const handleKeyDown = (e) => {
    // ── Waiting-to-confirm mode: Enter = save now, any other key = cancel ──
    if (waitingToConfirm) {
      if (e.key === 'Enter') {
        e.preventDefault();
        executeAutoSave();
        return;
      }
      // Any non-modifier key cancels waiting and keeps the magic word.
      // Let the keystroke be processed normally by the browser (it'll
      // append to the textarea and fire handleChange).
      if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') {
        cancelWaiting();
        // Don't preventDefault — let the character through
        return;
      }
      // For modifier-only keys (Shift, Ctrl, etc.), ignore during waiting
      return;
    }

    // Ctrl+Enter / Cmd+Enter to confirm
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleConfirm();
    }
    // Escape to close
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  return {
    // state
    content,
    contentRef,
    setContent,
    saving,
    waitingToConfirm,
    timelineMenuOpen,
    setTimelineMenuOpen,
    isEdit,

    // refs
    textareaRef,
    tlMenuRef,

    // actions
    handleClose,
    handleConfirm,
    cancelWaiting,
    executeAutoSave,
    handleChange,
    handleKeyDown,
    handleCompositionStart,
    handleCompositionEnd,
  };
}
